/**
 * Production API origin: prefer /runtime-config.json (deploy-time), then VITE_API_URL from generated-env.
 * No hardcoded production URL — build must supply public/runtime-config.json (see ensure-runtime-config.cjs).
 * Dev always uses same-origin /api via Vite proxy (see passportApi getBaseUrl).
 */
import { VITE_API_URL } from './generated-env';

const CONFIG_DOC = 'docs/PASSPORT_PRODUCTION_API_AUDIT.md';

let productionApiBaseUrl: string | undefined;

/** DR cutover (Phase 4 client failover): which Firebase project the app should use. */
export type ActiveFirebaseProject = 'primary' | 'secondary';
let activeFirebaseProject: ActiveFirebaseProject = 'primary';

/**
 * Active Firebase project resolved from /runtime-config.json at bootstrap.
 * Defaults to "primary" (also in dev). firebase.ts reads this when lazily
 * initializing, so cutover = edit runtime-config.json + reload (no rebuild),
 * and only takes effect if a complete secondary config is bundled.
 */
export function getActiveFirebaseProject(): ActiveFirebaseProject {
  return activeFirebaseProject;
}

function stripTrailingSlash(u: string): string {
  return u.replace(/\/$/, '');
}

/**
 * Passport always requests `{origin}/api/passport/...` and `{origin}/api/auth/...`.
 * If `runtime-config.json` mistakenly includes a path (e.g. `/api`, `/compliance`, `/fmcsa-authority`),
 * fetches can hit non-existent routes (404 ROUTE_NOT_FOUND). Origin-only prevents that.
 */
function toApiOrigin(url: string): string {
  const t = url.trim();
  if (!t) return t;
  try {
    const withScheme = /^https?:\/\//i.test(t) ? t : `https://${t}`;
    return new URL(withScheme).origin;
  } catch {
    return stripTrailingSlash(t);
  }
}

/** True if URL points at loopback — unusable for public users hitting passport.* from the browser. */
export function isLoopbackApiBase(url: string): boolean {
  const t = url.trim();
  if (!/^https?:\/\//i.test(t)) return false;
  try {
    const u = new URL(t);
    const h = u.hostname.toLowerCase();
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '0.0.0.0';
  } catch {
    return false;
  }
}

function normalizeApiBaseString(raw: string): string {
  let url = raw.trim();
  if (!url) return '';
  if (/^\d+$/.test(url)) url = `http://localhost:${url}`;
  else if (!/^https?:\/\//i.test(url)) url = `http://localhost:${url}`;
  return stripTrailingSlash(url);
}

async function tryLoadRuntimeApiBase(): Promise<string | null> {
  try {
    const res = await fetch('/runtime-config.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const raw = (await res.text()).trim();
    // Firebase catch-all rewrite can return index.html (200) if runtime-config.json was not deployed.
    if (!raw.startsWith('{')) return null;
    const data = JSON.parse(raw) as { apiBaseUrl?: unknown; activeFirebaseProject?: unknown };
    // Capture DR cutover selector regardless of apiBaseUrl validity.
    if (data.activeFirebaseProject === 'secondary') {
      activeFirebaseProject = 'secondary';
    }
    if (typeof data.apiBaseUrl !== 'string') return null;
    const normalized = normalizeApiBaseString(data.apiBaseUrl);
    if (!normalized) return null;
    if (isLoopbackApiBase(normalized)) {
      console.warn('[Passport] Ignoring loopback apiBaseUrl in runtime-config.json');
      return null;
    }
    return toApiOrigin(normalized);
  } catch {
    return null;
  }
}

/**
 * Load once before React mounts. In production, resolves the backend origin for fetch() calls.
 */
export async function loadPassportConfig(): Promise<void> {
  if (import.meta.env.DEV) {
    productionApiBaseUrl = undefined;
    return;
  }

  const fromRuntime = await tryLoadRuntimeApiBase();
  if (fromRuntime) {
    productionApiBaseUrl = fromRuntime;
    return;
  }

  const viteRaw = (VITE_API_URL || '').trim();
  const fromVite = viteRaw ? normalizeApiBaseString(viteRaw) : '';
  if (fromVite && !isLoopbackApiBase(fromVite)) {
    productionApiBaseUrl = toApiOrigin(fromVite);
    return;
  }

  if (import.meta.env.PROD) {
    throw new Error(
      '[Passport] Could not resolve API origin in production. /runtime-config.json was missing, invalid, or ' +
        'returned HTML (check hosting rewrites), and VITE_API_URL is unset or points to loopback. ' +
        'Rebuild with PASSPORT_API_ORIGIN or BACKEND_URL set (see ' +
        CONFIG_DOC +
        ').'
    );
  }

  throw new Error(
    '[Passport] API base URL missing. See ' + CONFIG_DOC + ' and Passport/.env.example.'
  );
}

/** Backend origin for API calls after loadPassportConfig() (production only). */
export function getProductionApiBaseUrl(): string {
  if (import.meta.env.DEV) return '';
  const v = productionApiBaseUrl;
  if (v === undefined) {
    throw new Error('[Passport] Config not loaded: loadPassportConfig() must run before API calls.');
  }
  return v;
}
